import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Container, Row, Col, Card, Button, Badge, Modal, 
  Form, Alert, Spinner, ButtonGroup, Accordion, 
  Table, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import clientService from 'services/clientService';
import { useAuth } from 'contexts/AuthContext';

// Components available in GalaxyMetrics
import FalconCardHeader from '../common/FalconCardHeader';
import Flex from '../common/Flex';
import SubtleBadge from '../common/SubtleBadge';

// Función para validar si un registro de cuenta es válido (MUY permisiva para debug)
const isValidAccount = (account) => {
  if (!account) {

    return false;
  }
  
  
  
  // Por ahora, aceptar CUALQUIER cuenta que tenga ID
  return !!account.AccountID;
};

// Función para limpiar y formatear el nombre de cuenta de Meta
const formatAccountName = (accountName, accountType, accountIdentifier) => {
  if (!accountName) return '';
  
  // Para Meta, ocultar el prefijo "act_" del identificador si está en el nombre
  if (accountType === 'Meta' && accountIdentifier && accountIdentifier.startsWith('act_')) {
    const cleanId = accountIdentifier.replace('act_', '');
    return accountName.replace(accountIdentifier, cleanId);
  }
  
  return accountName;
};

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'Google', label: 'Google Ads' },
  { value: 'Meta', label: 'Meta Ads' },
  { value: 'TikTok', label: 'TikTok Ads' },
  { value: 'LinkedIn', label: 'LinkedIn Ads' },
  { value: 'Illumin', label: 'Illumin (CTV/Programmatic)' }
];

const getAccountIdentifierPlaceholder = (accountType) => {
  switch (accountType) {
    case 'Meta':
      return 'Ej: 123456789 (sin act_)';
    case 'TikTok':
      return 'Ej: 7139886330355794946';
    case 'LinkedIn':
      return 'Ej: 512345678';
    case 'Illumin':
      return 'Ej: 37683 (Advertiser ID de Illumin)';
    default:
      return 'Ej: 9876543210';
  }
};

const getAccountTypeLabel = (accountType) => {
  const option = ACCOUNT_TYPE_OPTIONS.find(item => item.value === accountType);
  return option?.label || accountType;
};

const AccountManagement = () => {
  const { filterByAssignedClients } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [allClients, setAllClients] = useState([]); // Lista de clientes (filtrada por asignación)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [currentClient, setCurrentClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeAccordionKey, setActiveAccordionKey] = useState(['0']);
  
  // Estados para modal de eliminación de cuentas
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeactivateClientModal, setShowDeactivateClientModal] = useState(false);
  const [clientToDeactivate, setClientToDeactivate] = useState(null);
  const [deactivationInfo, setDeactivationInfo] = useState(null);
  
  // Estados para modal de eliminación física de clientes
  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [clientDeletionInfo, setClientDeletionInfo] = useState(null);

  // Estados de loading específicos para cada acción
  const [loadingActions, setLoadingActions] = useState({
    toggleStatus: new Set(), // Set de IDs de cuentas que están siendo activadas/desactivadas
    updating: new Set(),     // Set de IDs de cuentas que están siendo actualizadas
    clientUpdating: new Set(), // Set de IDs de clientes que están siendo actualizados
    clientDeleting: new Set()  // Set de IDs de clientes que están siendo eliminados
  });

  // Referencia para mantener posición del scroll
  const containerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Estados del formulario de cuenta
  const [accountFormData, setAccountFormData] = useState({
    ClientID: '',
    AccountType: 'Google',
    AccountIdentifier: '',
    AccountName: '',
    Active: true,
    CampaignType: 'Performance',
    FunnelStage: ''
  });

  // Estados del formulario de cliente
  const [clientFormData, setClientFormData] = useState({
    ClientName: '',
    Active: true,
    LoadSchedule: 'Daily',
    SupermetricsAPIKey: '',
    SupermetricsUser_Meta: '',
    SupermetricsUser_Google: '',
    DateRangeType: 'last_3_days',
    OtherConfigurations: ''
  });

  // Estado para controlar visibilidad del API key
  const [showApiKey, setShowApiKey] = useState(false);

  // Función para actualizar estados de loading
  const updateLoadingAction = (actionType, id, isLoading) => {
    setLoadingActions(prev => {
      const newState = { ...prev };
      if (isLoading) {
        newState[actionType] = new Set([...newState[actionType], id]);
      } else {
        newState[actionType] = new Set([...newState[actionType]].filter(item => item !== id));
      }
      return newState;
    });
  };

  // Función para verificar si una acción está en loading
  const isActionLoading = (actionType, id) => {
    return loadingActions[actionType].has(id);
  };

  // Cargar cuentas inicialmente usando API optimizado (clientes con cuentas agrupadas)
  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await clientService.getClientsWithAccounts();
      
      
      if (response && response.success && response.clients) {
        // Extraer clientes para el dropdown y filtrar por asignaciones del usuario
        const extractedClients = response.clients.map(client => ({
          ClientID: client.ClientID,
          ClientName: client.ClientName,
          Active: client.Active,
          LoadSchedule: client.LoadSchedule,
          SupermetricsAPIKey: client.SupermetricsAPIKey,
          SupermetricsUser_Meta: client.SupermetricsUser_Meta,
          SupermetricsUser_Google: client.SupermetricsUser_Google,
          DateRangeType: client.DateRangeType,
          OtherConfigurations: client.OtherConfigurations
        })).sort((a, b) => a.ClientName.localeCompare(b.ClientName));
        
        // Filtrar clientes por los asignados al usuario
        const filteredClients = filterByAssignedClients(extractedClients, 'ClientID');
        setAllClients(filteredClients);
        
        
        // Filtrar clientes de la respuesta original antes de extraer cuentas
        const filteredClientIds = new Set(filteredClients.map(c => c.ClientID));
        const clientsToProcess = response.clients.filter(c => filteredClientIds.has(c.ClientID));
        
        // Convertir estructura de clientes con cuentas a array plano de cuentas para compatibilidad
        const flatAccounts = clientsToProcess.flatMap(client => 
          client.accounts.map(account => ({
            ...account,
            client: {
              ...client // Preservar TODOS los campos del cliente
            }
          }))
        );
        
        const validAccounts = flatAccounts.filter(isValidAccount);
        
        setAccounts(validAccounts);
        
        if (validAccounts.length === 0) {
          toast.info('No hay cuentas válidas de Google, Meta, TikTok o LinkedIn para mostrar');
        }
      } else if (response && !response.success) {
        throw new Error(response.error || 'Error al cargar las cuentas');
      } else {
        throw new Error('Formato de respuesta inválido');
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
      
      let errorMessage = 'Error al cargar las cuentas';
      
      if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
        errorMessage = 'No se puede conectar con el servidor. Verifique que el backend esté ejecutándose.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Endpoint no encontrado. Verifique la URL del API.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Error interno del servidor.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  // Función para guardar y restaurar posición del scroll
  const saveScrollPosition = () => {
    if (containerRef.current) {
      setScrollPosition(containerRef.current.scrollTop);
    }
  };

  const restoreScrollPosition = () => {
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = scrollPosition;
      }
    }, 100);
  };

  // Actualizar cuenta localmente en el estado
  const updateAccountInState = (updatedAccount) => {
    setAccounts(prevAccounts => 
      prevAccounts.map(account => 
        account.AccountID === updatedAccount.AccountID 
          ? { ...account, ...updatedAccount }
          : account
      )
    );
  };

  // Eliminar cuenta localmente del estado
  const removeAccountFromState = (accountId) => {
    setAccounts(prevAccounts => 
      prevAccounts.filter(account => account.AccountID !== accountId)
    );
  };

  // Actualizar cliente y sus cuentas en cascada
  const updateClientInState = (updatedClient) => {
    setAccounts(prevAccounts => 
      prevAccounts.map(account => {
        if (account.ClientID === updatedClient.ClientID) {
          const updatedAccount = { 
            ...account, 
            client: { ...account.client, ...updatedClient }
          };
          
          // Si el cliente se desactiva, desactivar todas sus cuentas
          if (!updatedClient.Active && account.Active) {
            updatedAccount.Active = false;
          }
          
          return updatedAccount;
        }
        return account;
      })
    );
  };

  // Agregar nueva cuenta al estado
  const addAccountToState = (newAccount) => {
    setAccounts(prevAccounts => [...prevAccounts, newAccount]);
  };

  // *** NUEVO: Agrupar TODOS los clientes (con y sin cuentas) ***
  const groupedAccounts = useMemo(() => {
    if (!Array.isArray(accounts) || !Array.isArray(allClients)) return [];
    
    // Filtrar cuentas válidas
    const filteredAccounts = accounts.filter(account => {
      if (!isValidAccount(account)) return false;
      
      const matchesSearch = 
        (account.client?.ClientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (account.AccountName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (account.AccountIdentifier || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = 
        statusFilter === 'all' ||
        (statusFilter === 'active' && account.Active === true) ||
        (statusFilter === 'inactive' && account.Active === false);
      
      return matchesSearch && matchesStatus;
    });

    // Agrupar cuentas por cliente
    const accountsByClient = filteredAccounts.reduce((acc, account) => {
      const clientId = account.ClientID;
      if (!acc[clientId]) {
        acc[clientId] = [];
      }
      acc[clientId].push(account);
      return acc;
    }, {});

    // Crear grupos incluyendo TODOS los clientes (con y sin cuentas)
    const groups = allClients
      .filter(client => {
        // Filtrar clientes por búsqueda
        const matchesSearch = client.ClientName.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Si el cliente tiene cuentas, verificar si alguna pasa el filtro de estado
        const clientAccounts = accountsByClient[client.ClientID] || [];
        if (clientAccounts.length > 0) {
          const hasMatchingAccounts = statusFilter === 'all' || 
            clientAccounts.some(account => 
              (statusFilter === 'active' && account.Active === true) ||
              (statusFilter === 'inactive' && account.Active === false)
            );
          return matchesSearch && hasMatchingAccounts;
        }
        
        // Si no tiene cuentas, mostrar solo si el filtro es 'all' o 'inactive'
        return matchesSearch && (statusFilter === 'all' || statusFilter === 'inactive');
      })
      .map(client => ({
        clientId: client.ClientID,
        client: client,
        accounts: accountsByClient[client.ClientID] || []
      }));

    return groups;
  }, [accounts, allClients, searchTerm, statusFilter]);

  // *** NUEVO: Estadísticas incluyendo TODOS los clientes (con y sin cuentas) ***
  const stats = useMemo(() => {
    if (!Array.isArray(accounts) || !Array.isArray(allClients)) {
      return { total: 0, active: 0, inactive: 0, clients: 0, activeClients: 0 };
    }

    const validAccounts = accounts.filter(isValidAccount);
    
    return {
      total: validAccounts.length,
      active: validAccounts.filter(a => a.Active === true).length,
      inactive: validAccounts.filter(a => a.Active === false).length,
      clients: allClients.length, // *** AHORA cuenta TODOS los clientes, no solo los que tienen cuentas ***
      activeClients: allClients.filter(client => client.Active === true).length
    };
  }, [accounts, allClients]);

  // Obtener ícono y color por tipo de cuenta
  const getAccountTypeInfo = (accountType) => {
    switch (accountType) {
      case 'Google':
        return { icon: ['fab', 'google-plus-g'], variant: 'danger' };
      case 'Meta':
        return { icon: ['fab', 'facebook'], variant: 'primary' };
      case 'TikTok':
        return { icon: 'video', variant: 'dark' };
      case 'LinkedIn':
        return { icon: ['fab', 'linkedin-in'], variant: 'info' };
      case 'Illumin':
        return { icon: 'tv', variant: 'purple' };
      default:
        return { icon: 'question-circle', variant: 'secondary' };
    }
  };

  // Renderizar badge de tipo de cuenta
  const renderAccountTypeBadge = (accountType) => {
    const { icon, variant } = getAccountTypeInfo(accountType);
    
    if (accountType === 'Meta') {
      return (
        <SubtleBadge bg={variant} className="d-inline-flex align-items-center" style={{ width: 'fit-content' }}>
          <FontAwesomeIcon icon={icon} className="me-1" />
          Meta
        </SubtleBadge>
      );
    }
    
    if (accountType === 'Google') {
      return (
        <SubtleBadge bg={variant} className="d-inline-flex align-items-center" style={{ width: 'fit-content' }}>
          <FontAwesomeIcon icon={icon} className="me-1" />
          Google
        </SubtleBadge>
      );
    }

    if (accountType === 'TikTok') {
      return (
        <SubtleBadge bg={variant} className="d-inline-flex align-items-center" style={{ width: 'fit-content' }}>
          <FontAwesomeIcon icon={icon} className="me-1" />
          TikTok
        </SubtleBadge>
      );
    }

    if (accountType === 'LinkedIn') {
      return (
        <SubtleBadge bg={variant} className="d-inline-flex align-items-center" style={{ width: 'fit-content' }}>
          <FontAwesomeIcon icon={icon} className="me-1" />
          LinkedIn
        </SubtleBadge>
      );
    }
    
    // Fallback para otros tipos
    return (
      <SubtleBadge bg="secondary" className="d-inline-flex align-items-center" style={{ width: 'fit-content' }}>
                        <FontAwesomeIcon icon="question-circle" className="me-1" />
        {accountType}
      </SubtleBadge>
    );
  };

  // === GESTIÓN DE CUENTAS ===

  // Manejar envío del formulario de cuenta
  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    saveScrollPosition();

    try {
      if (!accountFormData.ClientID || !accountFormData.AccountName || !accountFormData.AccountIdentifier) {
        toast.error('Por favor complete todos los campos requeridos');
        return;
      }

      // Validar que ClientID sea un número válido
      const clientIdNumber = parseInt(accountFormData.ClientID, 10);
      if (isNaN(clientIdNumber)) {
        toast.error('El ID del cliente debe ser un número válido');
        return;
      }

      // Validar que AccountType esté seleccionado
      if (!accountFormData.AccountType || accountFormData.AccountType.trim() === '') {
        toast.error('Por favor seleccione un tipo de cuenta');
        return;
      }

      let accountIdentifier = accountFormData.AccountIdentifier;
      if (accountFormData.AccountType === 'Meta' && !accountIdentifier.startsWith('act_')) {
        accountIdentifier = `act_${accountIdentifier}`;
      }

      const payload = {
        ...accountFormData,
        ClientID: clientIdNumber, // ✅ Usar número validado
        AccountIdentifier: accountIdentifier
      };

      let response;
      if (currentAccount) {
        response = await clientService.updateAccount(currentAccount.AccountID, payload);
        if (response && response.success) {
          // Preferir los datos actualizados que devuelve el backend (incluye CampaignType y normalizaciones)
          const updatedFromServer = response.data || response.account || null;
          if (updatedFromServer) {
            updateAccountInState(updatedFromServer);
          } else {
            // Fallback: fusionar payload local
            const updatedAccount = { ...currentAccount, ...payload };
            updateAccountInState(updatedAccount);
          }
          // Solo mostrar notificación del componente, el servicio no debe mostrar toast
        }
      } else {
        response = await clientService.createAccount(payload);
        if (response && response.success) {

          toast.success('Cuenta creada exitosamente');
          setShowAccountModal(false);
          resetAccountForm();
          await loadAccounts();
          restoreScrollPosition();
          return; 
        }
      }
      
      if (response && !response.success) {
        throw new Error(response.error || 'Error al guardar la cuenta');
      }

      toast.success('Cuenta actualizada exitosamente');
      setShowAccountModal(false);
      resetAccountForm();
      restoreScrollPosition();
    } catch (error) {
      console.error('Error saving account:', error);
      toast.error(error.message || 'Error al guardar la cuenta');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resetear formulario de cuenta
  const resetAccountForm = () => {
    setAccountFormData({
      ClientID: '',
      AccountType: 'Google',
      AccountIdentifier: '',
      AccountName: '',
      Active: true,
      CampaignType: 'Performance',
      FunnelStage: ''
    });
    setCurrentAccount(null);
  };

  // Abrir modal para nueva cuenta
  const handleNewAccount = () => {
    resetAccountForm();
    setShowAccountModal(true);
  };

  // Abrir modal para editar cuenta
  const handleEditAccount = (account) => {
    setCurrentAccount(account);
    
    let displayIdentifier = account.AccountIdentifier || '';
    if (account.AccountType === 'Meta' && displayIdentifier.startsWith('act_')) {
      displayIdentifier = displayIdentifier.replace('act_', '');
    }
    
    setAccountFormData({
      ClientID: account.ClientID || '',
      AccountType: account.AccountType || 'Google',
      AccountIdentifier: displayIdentifier,
      AccountName: account.AccountName || '',
      Active: account.Active !== false,
      CampaignType: account.CampaignType || 'Performance',
      FunnelStage: account.FunnelStage || ''
    });
    setShowAccountModal(true);
  };

  // Alternar estado de cuenta
  const handleToggleAccountStatus = async (account) => {
    const accountId = account.AccountID;
    updateLoadingAction('toggleStatus', accountId, true);
    saveScrollPosition();
    
    try {
      const response = await clientService.toggleAccountStatus(accountId, !account.Active);
      
      if (response && response.success) {
        // Actualizar localmente sin recargar
        const updatedAccount = { ...account, Active: !account.Active };
        updateAccountInState(updatedAccount);
        toast.success(`Cuenta ${!account.Active ? 'activada' : 'desactivada'} exitosamente`);
      } else if (response && !response.success) {
        throw new Error(response.error || 'Error al cambiar el estado de la cuenta');
      }
      
      restoreScrollPosition();
    } catch (error) {
      console.error('Error toggling account status:', error);
      toast.error(error.message || 'Error al cambiar el estado de la cuenta');
    } finally {
      updateLoadingAction('toggleStatus', accountId, false);
    }
  };

  // Eliminar cuenta con confirmación detallada
  const handleDeleteAccount = (account) => {
    setAccountToDelete(account);
    setShowDeleteModal(true);
  };

  // Confirmar eliminación de cuenta
  const confirmDeleteAccount = async () => {
    setDeleting(true);
    saveScrollPosition();
    
    try {
      const response = await clientService.deleteAccount(accountToDelete.AccountID);
      
      if (response && response.success) {
        // Eliminar localmente sin recargar
        removeAccountFromState(accountToDelete.AccountID);
        toast.success('Cuenta eliminada exitosamente');
        setShowDeleteModal(false);
        setAccountToDelete(null);
      } else if (response && !response.success) {
        throw new Error(response.error || 'Error al eliminar la cuenta');
      }
      
      restoreScrollPosition();
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.message || 'Error al eliminar la cuenta');
    } finally {
      setDeleting(false);
    }
  };

  // Confirmar desactivación de cliente
  const confirmDeactivateClient = async () => {
    if (!clientToDeactivate || !deactivationInfo) return;

    updateLoadingAction('clientUpdating', clientToDeactivate.ClientID, true);
    saveScrollPosition();
    
    try {
      const payload = {
        ClientName: deactivationInfo.formData.ClientName.trim(),
        Active: false
      };

      const response = await clientService.updateClient(clientToDeactivate.ClientID, payload);
      
      if (response && response.success) {
        // Actualizar cliente y cuentas localmente
        const updatedClient = { ...clientToDeactivate, ...payload };
        updateClientInState(updatedClient);
        
        toast.success('Cliente y todas sus cuentas desactivadas exitosamente', {
          icon: '⚠️'
        });
        
        setShowDeactivateClientModal(false);
        setShowClientModal(false);
        setClientToDeactivate(null);
        setDeactivationInfo(null);
      } else if (response && !response.success) {
        throw new Error(response.error || 'Error al actualizar el cliente');
      }
      
      restoreScrollPosition();
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error(error.message || 'Error al desactivar el cliente');
    } finally {
      updateLoadingAction('clientUpdating', clientToDeactivate.ClientID, false);
    }
  };

  // === GESTIÓN DE CLIENTES ===

  // Abrir modal para nuevo cliente
  const handleNewClient = () => {
    setCurrentClient(null);
    setClientFormData({
      ClientName: '',
      Active: true,
      LoadSchedule: 'Daily',
      SupermetricsAPIKey: '',
      SupermetricsUser_Meta: '',
      SupermetricsUser_Google: '',
      DateRangeType: 'last_3_days',
      OtherConfigurations: ''
    });
    setShowApiKey(false);
    setShowClientModal(true);
  };

  // Abrir modal para editar cliente
  const handleEditClient = (client) => {
    setCurrentClient(client);
    setClientFormData({
      ClientName: client.ClientName || '',
      Active: client.Active !== false,
      LoadSchedule: client.LoadSchedule || 'Daily',
      SupermetricsAPIKey: client.SupermetricsAPIKey || '',
      SupermetricsUser_Meta: client.SupermetricsUser_Meta || '',
      SupermetricsUser_Google: client.SupermetricsUser_Google || '',
      DateRangeType: client.DateRangeType || 'last_3_days',
      OtherConfigurations: client.OtherConfigurations || ''
    });
    setShowApiKey(false); // Ocultar API key por seguridad
    setShowClientModal(true);
  };

  // Manejar envío del formulario de cliente
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    const isEditMode = !!currentClient;
    const clientId = currentClient?.ClientID;
    setIsSubmitting(true);
    if (isEditMode) {
      updateLoadingAction('clientUpdating', clientId, true);
    }
    saveScrollPosition();

    try {
      if (!clientFormData.ClientName.trim()) {
        toast.error('El nombre del cliente es requerido');
        return;
      }

      // Si se está editando y desactivando el cliente, mostrar modal de confirmación
      if (isEditMode && currentClient.Active && !clientFormData.Active) {
        const clientAccounts = accounts.filter(acc => acc.ClientID === currentClient.ClientID);
        const activeAccounts = clientAccounts.filter(acc => acc.Active);
        
        if (activeAccounts.length > 0) {
          // Preparar la información para el modal
          setClientToDeactivate(currentClient);
          setDeactivationInfo({
            activeAccountsCount: activeAccounts.length,
            formData: clientFormData
          });
          setShowDeactivateClientModal(true);
          return; // No continuar hasta que se confirme en el modal
        }
      }

      const payload = {
        ClientName: clientFormData.ClientName.trim(),
        Active: clientFormData.Active,
        LoadSchedule: clientFormData.LoadSchedule,
        SupermetricsAPIKey: clientFormData.SupermetricsAPIKey.trim(),
        SupermetricsUser_Meta: clientFormData.SupermetricsUser_Meta.trim(),
        SupermetricsUser_Google: clientFormData.SupermetricsUser_Google.trim(),
        DateRangeType: clientFormData.DateRangeType,
        OtherConfigurations: clientFormData.OtherConfigurations.trim() || null
      };



      let response;
      if (isEditMode) {
        // Actualizar cliente existente
        response = await clientService.updateClient(currentClient.ClientID, payload);
        
        if (response && response.success) {
          // *** RECARGAR DATOS DESDE EL SERVIDOR DESPUÉS DE ACTUALIZAR ***
          toast.success('Cliente actualizado exitosamente');
          await loadAccounts(); // Recargar para obtener los datos más recientes
          
        } else if (response && !response.success) {
          throw new Error(response.error || 'Error al actualizar el cliente');
        }
      } else {
        // Crear nuevo cliente
        response = await clientService.createClient(payload);
        
        if (response && response.success) {
          // Agregar nuevo cliente al estado local
          await loadAccounts(); // Recargar para obtener el cliente con sus cuentas
          
          toast.success('Cliente creado exitosamente');
        } else if (response && !response.success) {
          throw new Error(response.error || 'Error al crear el cliente');
        }
      }

      setShowClientModal(false);
      setCurrentClient(null);
      setShowApiKey(false); // Reset visibilidad del API key
      restoreScrollPosition();
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error(error.message || 'Error al actualizar el cliente');
    } finally {
      setIsSubmitting(false);
      if (isEditMode) {
        updateLoadingAction('clientUpdating', clientId, false);
      }
    }
  };

  // Manejar eliminación de cliente
  const handleDeleteClient = (client, clientAccounts) => {
    setClientToDelete(client);
    setClientDeletionInfo({
      accountsCount: clientAccounts.length,
      activeAccountsCount: clientAccounts.filter(acc => acc.Active).length,
      inactiveAccountsCount: clientAccounts.filter(acc => !acc.Active).length
    });
    setShowDeleteClientModal(true);
  };

  // Confirmar eliminación de cliente
  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;

    const clientId = clientToDelete.ClientID;
    updateLoadingAction('clientDeleting', clientId, true);
    saveScrollPosition();

    try {
      const response = await clientService.deleteClient(clientId);
      
      if (response && response.success) {
        // Eliminar cliente y todas sus cuentas del estado local
        setAccounts(prevAccounts => 
          prevAccounts.filter(account => account.ClientID !== clientId)
        );
        
        toast.success(response.message || `Cliente "${clientToDelete.ClientName}" eliminado exitosamente`);
        setShowDeleteClientModal(false);
        setClientToDelete(null);
        setClientDeletionInfo(null);
        restoreScrollPosition();
      } else {
        throw new Error(response.error || 'Error al eliminar el cliente');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(error.message || 'Error al eliminar el cliente');
    } finally {
      updateLoadingAction('clientDeleting', clientId, false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Cargando cuentas...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          {error}
          <Button variant="outline-danger" size="sm" className="ms-2" onClick={loadAccounts}>
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" ref={containerRef}>
      {/* Header */}
      <Card className="mb-4">
        <FalconCardHeader 
          title="Gestión de Cuentas Publicitarias" 
          titleTag="h4" 
          className="border-bottom"
          endEl={
            <div className="d-flex gap-2">
              <Button variant="success" onClick={handleNewClient}>
                <FontAwesomeIcon icon="building" className="me-2" />
                Nuevo Cliente
              </Button>
              <Button variant="primary" onClick={handleNewAccount}>
                <FontAwesomeIcon icon="plus" className="me-2" />
                Nueva Cuenta
              </Button>
            </div>
          }
        />
      </Card>

      {/* Estadísticas */}
      <Row className="mb-1">
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body>
              <Flex alignItems="center">
                <div className="bg-light border border-primary rounded-circle p-3 me-3">
                  <FontAwesomeIcon icon="chart-line" className="text-primary" size="lg" />
                </div>
                <div>
                  <h6 className="mb-1 text-700">Total Cuentas</h6>
                  <h3 className="mb-0 text-primary">{stats.total}</h3>
                </div>
              </Flex>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body>
              <Flex alignItems="center">
                <div className="bg-light border border-success rounded-circle p-3 me-3">
                  <FontAwesomeIcon icon="check-circle" className="text-success" size="lg" />
                </div>
                <div>
                  <h6 className="mb-1 text-700">Activas</h6>
                  <h3 className="mb-0 text-success">{stats.active}</h3>
                </div>
              </Flex>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body>
              <Flex alignItems="center">
                <div className="bg-light border border-warning rounded-circle p-3 me-3">
                  <FontAwesomeIcon icon="times-circle" className="text-warning" size="lg" />
                </div>
                <div>
                  <h6 className="mb-1 text-700">Inactivas</h6>
                  <h3 className="mb-0 text-warning">{stats.inactive}</h3>
                </div>
              </Flex>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body>
              <Flex alignItems="center">
                <div className="bg-light border border-info rounded-circle p-3 me-3">
                  <FontAwesomeIcon icon="users" className="text-info" size="lg" />
                </div>
                <div>
                  <h6 className="mb-1 text-700">Clientes</h6>
                  <h3 className="mb-0 text-info">{stats.clients}</h3>
                  <small className="text-muted">({stats.activeClients} activos)</small>
                </div>
              </Flex>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Buscar por cliente o cuenta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos los estados</option>
                <option value="active">Solo activas</option>
                <option value="inactive">Solo inactivas</option>
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              <span className="text-muted">
                Mostrando {groupedAccounts.length} de {stats.clients} clientes
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de Clientes y Cuentas */}
      {groupedAccounts.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon icon="search" size="3x" className="text-muted mb-3" />
            <h5 className="text-muted">No se encontraron clientes</h5>
            <p className="text-muted">No hay clientes que coincidan con los filtros seleccionados</p>
          </Card.Body>
        </Card>
      ) : (
        <Accordion 
          activeKey={activeAccordionKey} 
          onSelect={(keys) => setActiveAccordionKey(Array.isArray(keys) ? keys : [keys])}
          alwaysOpen
        >
          {groupedAccounts.map((group, index) => {
            const client = group.client || {};
            const clientAccounts = group.accounts || [];
            const activeAccounts = clientAccounts.filter(acc => acc.Active === true).length;
            const inactiveAccounts = clientAccounts.filter(acc => acc.Active === false).length;

            return (
              <Accordion.Item eventKey={index.toString()} key={group.clientId || index}>
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100 me-3">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-0 me-3">{client.ClientName || 'Cliente Desconocido'}</h6>
                        <Badge 
                          bg={client.Active ? 'success' : 'secondary'} 
                          className="me-2"
                        >
                          {client.Active ? 'Cliente Activo' : 'Cliente Inactivo'}
                        </Badge>
                      </div>
                      <small className="text-muted">
                        {clientAccounts.length} cuenta{clientAccounts.length !== 1 ? 's' : ''} 
                        {activeAccounts > 0 && ` • ${activeAccounts} activa${activeAccounts !== 1 ? 's' : ''}`}
                        {inactiveAccounts > 0 && ` • ${inactiveAccounts} inactiva${inactiveAccounts !== 1 ? 's' : ''}`}
                      </small>
                    </div>
                    <div className="d-flex align-items-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Gestionar cliente</Tooltip>}
                      >
                        <span
                          className={`btn btn-outline-secondary btn-sm me-2 d-flex align-items-center justify-content-center ${
                            isActionLoading('clientUpdating', group.clientId) ? 'disabled' : ''
                          }`}
                          style={{ 
                            minWidth: '36px', 
                            minHeight: '32px',
                            cursor: isActionLoading('clientUpdating', group.clientId) ? 'not-allowed' : 'pointer',
                            border: '1px solid #6c757d',
                            opacity: isActionLoading('clientUpdating', group.clientId) ? 0.6 : 1
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isActionLoading('clientUpdating', group.clientId)) {
                              handleEditClient(client);
                            }
                          }}
                        >
                          {isActionLoading('clientUpdating', group.clientId) ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FontAwesomeIcon icon="cog" />
                          )}
                        </span>
                      </OverlayTrigger>
                      
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Eliminar cliente (elimina también todas sus cuentas)</Tooltip>}
                      >
                        <span
                          className={`btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center ${
                            isActionLoading('clientDeleting', group.clientId) ? 'disabled' : ''
                          }`}
                          style={{ 
                            minWidth: '36px', 
                            minHeight: '32px',
                            cursor: isActionLoading('clientDeleting', group.clientId) ? 'not-allowed' : 'pointer',
                            border: '1px solid #dc3545',
                            opacity: isActionLoading('clientDeleting', group.clientId) ? 0.6 : 1
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isActionLoading('clientDeleting', group.clientId)) {
                              handleDeleteClient(client, clientAccounts);
                            }
                          }}
                        >
                          {isActionLoading('clientDeleting', group.clientId) ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FontAwesomeIcon icon="trash" />
                          )}
                        </span>
                      </OverlayTrigger>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {clientAccounts.length === 0 ? (
                    <Alert variant="info">
                      <FontAwesomeIcon icon="info-circle" className="me-2" />
                      No hay cuentas disponibles para este cliente
                    </Alert>
                  ) : (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th className="fw-bold text-dark">Cuenta</th>
                          <th className="fw-bold text-dark">Tipo</th>
                          <th className="fw-bold text-dark">Etapa Funnel</th>
                          <th className="fw-bold text-dark">Identificador</th>
                          <th className="fw-bold text-dark">Estado</th>
                          <th className="fw-bold text-dark text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientAccounts.map((account) => (
                          <tr key={account.AccountID}>
                            <td>
                              <div>
                                <div className="text-dark">
                                  {formatAccountName(account.AccountName, account.AccountType, account.AccountIdentifier)}
                                </div>
                                {client.Active === false && (
                                  <small className="text-warning d-flex align-items-center">
                                    <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                                    Cliente inactivo
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>{renderAccountTypeBadge(account.AccountType)}</td>
                            <td>
                              {account.FunnelStage ? (
                                <Badge bg={account.FunnelStage === 'Upper' ? 'info' : account.FunnelStage === 'Mid' ? 'warning' : 'success'} className="text-uppercase" style={{ fontSize: '0.7rem' }}>
                                  {account.FunnelStage === 'Upper' ? 'Upper (Notoriedad)' : account.FunnelStage === 'Mid' ? 'Mid (Consideración)' : 'Lower (Acciones)'}
                                </Badge>
                              ) : <span className="text-muted small">—</span>}
                            </td>
                            <td>
                              <div className="font-monospace bg-light rounded px-2 py-1 text-dark fw-medium" style={{ fontSize: '0.85rem', display: 'inline-block' }}>
                                {account.AccountType === 'Meta' && account.AccountIdentifier
                                  ? account.AccountIdentifier.replace('act_', '')
                                  : account.AccountIdentifier}
                              </div>
                            </td>
                            <td>
                              <Badge bg={account.Active ? 'success' : 'secondary'}>
                                {account.Active ? 'Activa' : 'Inactiva'}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <ButtonGroup size="sm">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Editar cuenta</Tooltip>}
                                >
                                  <Button
                                    variant="outline-primary"
                                    onClick={() => handleEditAccount(account)}
                                    disabled={isActionLoading('updating', account.AccountID)}
                                  >
                                    {isActionLoading('updating', account.AccountID) ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FontAwesomeIcon icon="edit" />
                                    )}
                                  </Button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>{account.Active ? 'Desactivar' : 'Activar'}</Tooltip>}
                                >
                                  <Button
                                    variant={account.Active ? 'outline-warning' : 'outline-success'}
                                    onClick={() => handleToggleAccountStatus(account)}
                                    disabled={isActionLoading('toggleStatus', account.AccountID)}
                                  >
                                    {isActionLoading('toggleStatus', account.AccountID) ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FontAwesomeIcon icon={account.Active ? 'ban' : 'play'} />
                                    )}
                                  </Button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Eliminar cuenta</Tooltip>}
                                >
                                  <Button
                                    variant="outline-danger"
                                    onClick={() => handleDeleteAccount(account)}
                                    disabled={isActionLoading('toggleStatus', account.AccountID) || isActionLoading('updating', account.AccountID)}
                                  >
                                    <FontAwesomeIcon icon="trash" />
                                  </Button>
                                </OverlayTrigger>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {/* Modal para Nueva/Editar Cuenta */}
      <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentAccount ? 'Editar Cuenta' : 'Nueva Cuenta'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAccountSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente *</Form.Label>
                  <Form.Select
                    value={accountFormData.ClientID}
                    onChange={(e) => setAccountFormData(prev => ({ ...prev, ClientID: e.target.value }))}
                    required
                  >
                    <option value="">Seleccionar cliente...</option>
                    {allClients.map(client => (
                          <option key={client.ClientID} value={client.ClientID}>
                            {client.ClientName} {!client.Active && '(Inactivo)'}
                          </option>
                        ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Cuenta *</Form.Label>
                  <Form.Select
                    value={accountFormData.AccountType}
                    onChange={(e) => setAccountFormData(prev => ({ ...prev, AccountType: e.target.value }))}
                    required
                  >
                    {ACCOUNT_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Campaña (Branding/Performance)</Form.Label>
                  <Form.Select
                    value={accountFormData.CampaignType}
                    onChange={(e) => setAccountFormData(prev => ({ ...prev, CampaignType: e.target.value }))}
                  >
                    <option value="Performance">Performance</option>
                    <option value="Branding">Branding</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Etapa del Funnel</Form.Label>
                  <Form.Select
                    value={accountFormData.FunnelStage}
                    onChange={(e) => setAccountFormData(prev => ({ ...prev, FunnelStage: e.target.value }))}
                  >
                    <option value="">Sin asignar</option>
                    <option value="Upper">Upper (Notoriedad)</option>
                    <option value="Mid">Mid (Consideración)</option>
                    <option value="Lower">Lower (Acciones de valor)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Identificador de Cuenta *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={accountFormData.AccountIdentifier}
                    onChange={(e) => setAccountFormData(prev => ({ ...prev, AccountIdentifier: e.target.value }))}
                    placeholder={getAccountIdentifierPlaceholder(accountFormData.AccountType)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de la Cuenta *</Form.Label>
                  <Form.Control
                    type="text"
                    value={accountFormData.AccountName}
                    onChange={(e) => setAccountFormData(prev => ({ ...prev, AccountName: e.target.value }))}
                    placeholder="Nombre descriptivo de la cuenta"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Cuenta activa"
                checked={accountFormData.Active}
                onChange={(e) => setAccountFormData(prev => ({ ...prev, Active: e.target.checked }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAccountModal(false)}>
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                currentAccount ? 'Actualizar Cuenta' : 'Crear Cuenta'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para Crear/Editar Cliente */}
      <Modal show={showClientModal} onHide={() => { setShowClientModal(false); setShowApiKey(false); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={currentClient ? "edit" : "building"} className="me-2 text-primary" />
            {currentClient ? `Editar Cliente: ${currentClient.ClientName}` : 'Crear Nuevo Cliente'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleClientSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre del Cliente *</Form.Label>
                  <Form.Control
                    type="text"
                    value={clientFormData.ClientName}
                    onChange={(e) => setClientFormData(prev => ({ ...prev, ClientName: e.target.value }))}
                    placeholder="Nombre del cliente"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Programación de Carga</Form.Label>
                  <Form.Select
                    value={clientFormData.LoadSchedule}
                    onChange={(e) => setClientFormData(prev => ({ ...prev, LoadSchedule: e.target.value }))}
                  >
                    <option value="Daily">Diario</option>
                    <option value="Weekly">Semanal</option>
                    <option value="Monthly">Mensual</option>
                    <option value="Manual">Manual</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Rango de Fechas</Form.Label>
                  <Form.Select
                    value={clientFormData.DateRangeType}
                    onChange={(e) => setClientFormData(prev => ({ ...prev, DateRangeType: e.target.value }))}
                  >
                    <option value="yesterday">Ayer</option>
                    <option value="last_3_days">Últimos 3 días</option>
                    <option value="last_7_days">Últimos 7 días</option>
                    <option value="last_14_days">Últimos 14 días</option>
                    <option value="last_30_days">Últimos 30 días</option>
                    <option value="this_month">Este mes</option>
                    <option value="last_month">Mes pasado</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Cliente activo"
                    checked={clientFormData.Active}
                    onChange={(e) => setClientFormData(prev => ({ ...prev, Active: e.target.checked }))}
                  />
                  {!clientFormData.Active && (
                    <Form.Text className="text-warning">
                      <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                      Al desactivar el cliente, todas sus cuentas también se desactivarán
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Supermetrics API Key</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={showApiKey ? "text" : "password"}
                  value={clientFormData.SupermetricsAPIKey}
                  onChange={(e) => setClientFormData(prev => ({ ...prev, SupermetricsAPIKey: e.target.value }))}
                  placeholder="API Key de Supermetrics"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowApiKey(!showApiKey)}
                  style={{ borderLeft: 0 }}
                >
                  <FontAwesomeIcon icon={showApiKey ? "eye-slash" : "eye"} />
                </Button>
              </div>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Usuario Supermetrics - Meta</Form.Label>
                  <Form.Control
                    type="text"
                    value={clientFormData.SupermetricsUser_Meta}
                    onChange={(e) => setClientFormData(prev => ({ ...prev, SupermetricsUser_Meta: e.target.value }))}
                    placeholder="ID de usuario para Meta Ads"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Usuario Supermetrics - Google</Form.Label>
                  <Form.Control
                    type="email"
                    value={clientFormData.SupermetricsUser_Google}
                    onChange={(e) => setClientFormData(prev => ({ ...prev, SupermetricsUser_Google: e.target.value }))}
                    placeholder="Email para Google Ads"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Otras Configuraciones (JSON)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={clientFormData.OtherConfigurations}
                onChange={(e) => setClientFormData(prev => ({ ...prev, OtherConfigurations: e.target.value }))}
                placeholder='{"TikTok":"7139886330355794946"}'
              />
              <Form.Text className="text-muted">
                Configuraciones adicionales en formato JSON (opcional)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
                            <Button variant="secondary" onClick={() => { setShowClientModal(false); setShowApiKey(false); }}>
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                currentClient ? 'Actualizar Cliente' : 'Crear Cliente'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de Confirmación de Eliminación */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2 text-warning" />
            Confirmar Eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Estás seguro de que deseas eliminar esta cuenta publicitaria?</p>
          {accountToDelete && (
            <div className="bg-light p-3 rounded">
              <strong>Cuenta:</strong> {formatAccountName(accountToDelete.AccountName, accountToDelete.AccountType, accountToDelete.AccountIdentifier)}<br/>
              <strong>Cliente:</strong> {accountToDelete.client?.ClientName || 'Cliente desconocido'}<br/>
              <strong>Tipo:</strong> {getAccountTypeLabel(accountToDelete.AccountType)}<br/>
              <strong>ID:</strong> {accountToDelete.AccountType === 'Meta' && accountToDelete.AccountIdentifier 
                ? accountToDelete.AccountIdentifier.replace('act_', '') 
                : accountToDelete.AccountIdentifier}
            </div>
          )}
          <p className="text-danger mt-3 mb-0">
            <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
            Esta acción no se puede deshacer.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            <FontAwesomeIcon icon="times" className="me-1" />
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDeleteAccount}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Eliminando...</span>
                </div>
                Eliminando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="trash" className="me-1" />
                Eliminar Cuenta
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmación de Desactivación de Cliente */}
      <Modal show={showDeactivateClientModal} onHide={() => setShowDeactivateClientModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2 text-warning" />
            DESACTIVAR CLIENTE
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Estás seguro de que deseas desactivar este cliente?</p>
          {clientToDeactivate && deactivationInfo && (
            <div className="bg-light p-3 rounded">
              <strong>🏢 Cliente:</strong> {clientToDeactivate.ClientName}<br/>
              <strong>📊 Cuentas activas que se desactivarán:</strong> {deactivationInfo.activeAccountsCount}
            </div>
          )}
          <div className="mt-3 p-3 bg-warning-subtle border-warning border rounded">
            <h6 className="text-warning mb-2">
              <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
              CONSECUENCIAS:
            </h6>
            <ul className="mb-0 text-dark">
              <li>Todas las {deactivationInfo?.activeAccountsCount || 0} cuentas activas se desactivarán automáticamente</li>
              <li>No se cargarán más datos de ninguna cuenta de este cliente</li>
              <li>Los reportes dejarán de mostrar métricas nuevas de este cliente</li>
              <li>El cliente aparecerá como "Inactivo" en todas las vistas</li>
            </ul>
          </div>
          <p className="text-success mt-3 mb-0">
            <FontAwesomeIcon icon="info-circle" className="me-1" />
            Esta acción se puede revertir reactivando el cliente.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowDeactivateClientModal(false)}
            disabled={isActionLoading('clientUpdating', clientToDeactivate?.ClientID)}
          >
            <FontAwesomeIcon icon="times" className="me-1" />
            Cancelar
          </Button>
          <Button 
            variant="warning" 
            onClick={confirmDeactivateClient}
            disabled={isActionLoading('clientUpdating', clientToDeactivate?.ClientID)}
          >
            {isActionLoading('clientUpdating', clientToDeactivate?.ClientID) ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Desactivando...</span>
                </div>
                Desactivando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="ban" className="me-1" />
                Desactivar Cliente
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmación de Eliminación Física de Cliente */}
      <Modal show={showDeleteClientModal} onHide={() => setShowDeleteClientModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
            Eliminar Cliente Físicamente
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clientToDelete && (
            <>
              <Alert variant="danger" className="mb-3">
                <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                <strong>¡ATENCIÓN!</strong> Esta acción es IRREVERSIBLE.
              </Alert>
              
              <div className="mb-3">
                <strong>Cliente a eliminar:</strong>
                <div className="bg-light p-2 rounded mt-1">
                  <div className="fw-bold">{clientToDelete.ClientName}</div>
                  <small className="text-muted">ID: {clientToDelete.ClientID}</small>
                </div>
              </div>

              {clientDeletionInfo && (
                <div className="mb-3">
                  <strong>Cuentas que se eliminarán:</strong>
                  <ul className="mb-2">
                    <li>Total de cuentas: <strong>{clientDeletionInfo.accountsCount}</strong></li>
                    <li>Cuentas activas: <strong>{clientDeletionInfo.activeAccountsCount}</strong></li>
                    <li>Cuentas inactivas: <strong>{clientDeletionInfo.inactiveAccountsCount}</strong></li>
                  </ul>
                </div>
              )}

              <Alert variant="warning" className="mb-3">
                <FontAwesomeIcon icon="info-circle" className="me-2" />
                <strong>Consecuencias de esta acción:</strong>
                <ul className="mt-2 mb-0">
                  <li>Se eliminará el cliente de la configuración</li>
                  <li>Se eliminarán TODAS las cuentas asociadas</li>
                  <li>Se eliminarán todos los datos históricos</li>
                  <li>Se eliminarán datos dimensionales relacionados</li>
                  <li>Esta operación NO se puede deshacer</li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteClientModal(false)}
            disabled={isActionLoading('clientDeleting', clientToDelete?.ClientID)}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDeleteClient}
            disabled={isActionLoading('clientDeleting', clientToDelete?.ClientID)}
          >
            {isActionLoading('clientDeleting', clientToDelete?.ClientID) ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Eliminando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="trash" className="me-1" />
                Eliminar Permanentemente
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AccountManagement; 